import { Box, ButtonBase, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useErrorBoundary } from "react-error-boundary";
import classes from './StatisticsDashboard.module.scss';
import StatisticsService from "../../services/statistics.service";
import DateHelper, { CHART_DATE_FORMAT } from "../../helpers/date.helper";
import { ContentStatistic } from "../../interfaces/ContentStatistic";
import { EntityNames } from "../../enums/EntityNames";
import { ContentStatisticData } from "../../interfaces/ContentStatisticData";
import RangePicker from "../range-picker/RangePicker";
import { DateRange } from "react-day-picker";
import AreaChart from "../../views/charts/area-chart/AreaChart";
import MixedBarChart from "../../views/charts/bar-chart/MixedBarChart";
import useCounterAnimation from "../../hooks/useCounterAnimation";
import SitesStatistic from "../../views/sites-statistic/SitesStatistic";
import { ArticleTypes } from "../../enums/ArticleTypes";
import { TypeStatisticsItem } from "../types/TypeStatisticItem";
import FillMissingDaysHelper from "../../helpers/fill-missing-days.helper";

const initialDateRange = {
  start: DateHelper.getFirstDayOfPreviousMonth(),
  end: dayjs().format('YYYY-MM-DD')
};

const StatisticsDashboard = () => {
  const [statisticsData, setStatisticsData] = useState<ContentStatisticData>({
    typesContentData: [],
    typesVariationsData: [],
    sitesData: [],
    contentData: [],
    variationsData: [],
    totalContentPieces: 0,
    totalVariations: 0
  });
  const [date, setDate] = useState<{ start: string; end: string }>(initialDateRange);
  const [selectedAreaChart, setSelectedAreaChart] = useState<ArticleTypes>(ArticleTypes.ORIGINAL_ARTICLES);
  const [selectedBarChart, setSelectedBarChart] = useState<ArticleTypes>(ArticleTypes.ORIGINAL_ARTICLES);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const animatedTotalContentPieces = useCounterAnimation(statisticsData.totalContentPieces);
  const animatedTotalVariations = useCounterAnimation(statisticsData.totalVariations);
  const { showBoundary } = useErrorBoundary();
  const setRange = (range: DateRange) => {
    if (range.from && range.to) {
      setDate({ start: DateHelper.getDateFormatted(range.from), end: DateHelper.getDateFormatted(range.to) });
    }
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const dataPromises = [
          StatisticsService.getTypesStatisticsByDateRange(date.start, date.end, 'contentPieces'),
          StatisticsService.getEntityStatisticsByDateRange(date.start, date.end, EntityNames.SITES),
          StatisticsService.getEntityStatisticsByDateRange(date.start, date.end, EntityNames.CONTENT_PIECES),
          StatisticsService.getEntityStatisticsByDateRange(date.start, date.end, EntityNames.VARIATIONS)
        ];
        const [typesContentData, sitesData, contentData, variationsData] = await Promise.all(dataPromises);
        const formattedContentTypesData = FillMissingDaysHelper.fillMissingTypeDates(typesContentData.statistics, date.start, date.end).map((content: TypeStatisticsItem) => ({
          ...content,
          date: dayjs(content.date).format(CHART_DATE_FORMAT)
        }));
        const formattedContentData = FillMissingDaysHelper.fillMissingContentDates(contentData.statistics, date.start, date.end).map((content: ContentStatistic) => ({
          date: dayjs(content.date).format(CHART_DATE_FORMAT),
          count: content.count,
        }));
        const formattedVariationsData = FillMissingDaysHelper.fillMissingContentDates(variationsData.statistics, date.start, date.end).map((variation: ContentStatistic) => ({
          date: dayjs(variation.date).format(CHART_DATE_FORMAT),
          count: variation.count,
        }));
        const countTotalContentPieces = contentData.statistics.reduce((accumulator, content: ContentStatistic) => accumulator + content.count, 0);
        const countTotalVariations = variationsData.statistics.reduce((accumulator, variation: ContentStatistic) => accumulator + variation.count, 0);
        setStatisticsData((prevStatisticsData) => ({
          ...prevStatisticsData,
          typesContentData: formattedContentTypesData,
          sitesData: sitesData.statistics,
          contentData: formattedContentData,
          variationsData: formattedVariationsData,
          totalContentPieces: countTotalContentPieces,
          totalVariations: countTotalVariations
        }));
      } catch (error) {
        showBoundary(error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [date, showBoundary])

  const onTypeVariationsClickHandler = useCallback(() => {
    StatisticsService.getTypesStatisticsByDateRange(date.start, date.end, 'variations')
      .then((data) => {
        const formattedVariationsTypesData = data.statistics.map((variation: TypeStatisticsItem) => ({
          ...variation,
          date: dayjs(variation.date).format(CHART_DATE_FORMAT)
        }));
        setStatisticsData((prevStatisticsData) => ({ ...prevStatisticsData, typesVariationsData: formattedVariationsTypesData }));
        setSelectedBarChart(ArticleTypes.VARIATIONS);
      })
      .catch((error) => showBoundary(error));
  }, [date, showBoundary]);

  return (
    <Box className='d-flex flex-column gap-5'>
      <Typography className='h3'>
        Statistic
      </Typography>
      <Box className='d-flex flex-column flex-sm-row justify-content-between gap-5' >
        <RangePicker
          isModal={false}
          getRange={setRange}
          initialDateRange={{ from: new Date(DateHelper.getFirstDayOfPreviousMonth()), to: new Date() }}
        />
        <Box className='d-flex w-100 justify-content-between flex-column'>
          <Box className={`${classes['total-box']} rounded`} >
            <Box className='d-flex justify-content-between flex-row p-5 align-items-center'>
              <Typography className={classes['total-label']} >
                Total Number of Original Articles
              </Typography>
              {isLoading ? <CircularProgress color='success' size='4.5rem' />
                :
                <Typography className={classes['total-number']} >
                  {animatedTotalContentPieces}
                </Typography>
              }
            </Box>
          </Box>
          <Box className={`${classes['total-box']} rounded`} >
            <Box className='d-flex justify-content-between flex-row p-5 align-items-center'>
              <Typography className={classes['total-label']} >
                Total Number of Variations
              </Typography>
              {isLoading ? <CircularProgress color='success' size='4.5rem' />
                :
                <Typography className={classes['total-number']} >
                  {animatedTotalVariations}
                </Typography>
              }
            </Box>
          </Box>
        </Box>
      </Box>
      <Box>
        <Box className='mb-3'>
          <ButtonBase
            value={ArticleTypes.ORIGINAL_ARTICLES}
            onClick={() => setSelectedAreaChart(ArticleTypes.ORIGINAL_ARTICLES)}
            className={`h5 px-2 me-3 ${selectedAreaChart === ArticleTypes.ORIGINAL_ARTICLES ? classes.active : classes['not-selected']}`}
          >Original Articles</ButtonBase>
          <ButtonBase
            value={ArticleTypes.VARIATIONS}
            onClick={() => setSelectedAreaChart(ArticleTypes.VARIATIONS)}
            className={`h5 px-2 me-3 ${selectedAreaChart === ArticleTypes.VARIATIONS ? classes.active : classes['not-selected']}`}
          >Variations</ButtonBase>
        </Box>
        {selectedAreaChart === ArticleTypes.ORIGINAL_ARTICLES ?
          <AreaChart data={statisticsData.contentData} tooltipName='Original Articles' />
          :
          <AreaChart data={statisticsData.variationsData} tooltipName='Variations' />
        }
      </Box>
      <Box>
        <Box className='mb-3'>
          <ButtonBase
            value={ArticleTypes.ORIGINAL_ARTICLES}
            onClick={() => setSelectedBarChart(ArticleTypes.ORIGINAL_ARTICLES)}
            className={`h5 px-2 me-3 ${selectedBarChart === ArticleTypes.ORIGINAL_ARTICLES ? classes.active : classes['not-selected']}`}
          >Original Articles</ButtonBase>
          <ButtonBase
            value={ArticleTypes.VARIATIONS}
            onClick={onTypeVariationsClickHandler}
            className={`h5 px-2 me-3 ${selectedBarChart === ArticleTypes.VARIATIONS ? classes.active : classes['not-selected']}`}
          >Variations</ButtonBase>
        </Box>
        {selectedBarChart === ArticleTypes.ORIGINAL_ARTICLES ?
          <MixedBarChart data={statisticsData.typesContentData} />
          :
          <MixedBarChart data={statisticsData.typesVariationsData} />
        }
      </Box>
      <SitesStatistic data={statisticsData.sitesData} />
    </Box>
  );
}

export default StatisticsDashboard;
